import React, { useState, useEffect } from 'react';
import { StoryBible, Chapter, PublicationSettings, ExportRecord } from '../src/types';
import { generatePublication } from '../services/publicationFormatter.service';
import { collection, query, orderBy, getDocs, doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../src/firebaseConfig';
import { loadChapterContentFromStorage, resolveChapterStoragePath, saveChapterContentToStorage, StorageChapter } from '../services/storageChapter.service';
import Tooltip from './Tooltip';

interface PublicationExportProps {
  uid: string;
  projectId: string;
  storyBible: StoryBible;
  chapters: Chapter[];
  onUpdateChapters?: (chapters: Chapter[]) => void;
  onRepairRequested?: () => void;
  onClose: () => void;
}

export const PublicationExport: React.FC<PublicationExportProps> = ({ uid, projectId, storyBible, chapters, onUpdateChapters, onRepairRequested, onClose }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState('');
  const [exportUrl, setExportUrl] = useState('');
  const [previousExports, setPreviousExports] = useState<ExportRecord[]>([]);

  const [diagnostics, setDiagnostics] = useState({
    chaptersWithMetadata: 0,
    chaptersWithResolvedStoragePath: 0,
    chaptersWithReadableStorageJson: 0,
    chaptersWithFinalText: 0,
    chaptersRepairableFromFinalText: 0,
    chaptersTrulyMissing: 0,
  });

  const [warnings, setWarnings] = useState<string[]>([]);
  const [checkTrigger, setCheckTrigger] = useState(0);
  const [isRepairing, setIsRepairing] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [repairStatus, setRepairStatus] = useState('');

  useEffect(() => {
    const fetchExports = async () => {
      try {
        const exportsRef = collection(db, `users/${uid}/projects/${projectId}/exports`);
        const q = query(exportsRef, orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        const records = snap.docs.map(d => d.data() as ExportRecord);
        setPreviousExports(records);
      } catch (err) {
        console.error('Failed to fetch exports', err);
      }
    };
    fetchExports();
  }, [uid, projectId]);

  const [settings, setSettings] = useState<PublicationSettings>({
    bookTitle: storyBible.title || '',
    authorName: '',
    copyrightYear: new Date().getFullYear().toString(),
    publisherName: '',
    dedication: '',
    acknowledgments: '',
    aboutAuthor: '',
    alsoBy: '',
    exportFormat: 'DOCX',
    headingStyle: 'CENTERED',
    paragraphStyle: 'INDENTED',
    sceneBreakStyle: 'ASTERISKS',
    includeTitlePage: true,
    includeCopyrightPage: true,
    includeToc: true,
    includeDedication: false,
    includeAcknowledgments: false,
    includeAboutAuthor: false,
    includeAlsoBy: false,
  });

  const [readiness, setReadiness] = useState({
    isChecking: true,
    totalExpected: storyBible.targetChapterCount,
    chaptersFound: chapters.length,
    chaptersWithFinalText: 0,
    missingFiles: [] as number[],
    missingFinalText: [] as number[],
  });

  useEffect(() => {
    let active = true;
    const checkReadiness = async () => {
      setReadiness(prev => ({ ...prev, isChecking: true }));
      let withFinal = 0;
      const missingFilesList: number[] = [];
      const missingFinalList: number[] = [];
      const warningsList: string[] = [];
      
      let countWithMetadata = chapters.length;
      let countWithResolvedPath = 0;
      let countWithReadableJson = 0;
      let countWithFinalText = 0;
      let countRepairable = 0;
      let countTrulyMissing = 0;

      const expectedTotal = storyBible.targetChapterCount || chapters.length;
      const scanLimit = Math.max(chapters.length, expectedTotal);

      const promises = Array.from({length: scanLimit}, async (_, i) => {
        const cNum = i + 1;
        const chap = chapters.find(c => c.id === cNum || c.chapterNumber === cNum);
        
        let storageData: StorageChapter | null = null;
        let isReadable = false;

        const pathsToTry: string[] = [];
        
        if (chap) {
          const resolvedPath = resolveChapterStoragePath(chap);
          if (resolvedPath) {
            pathsToTry.push(resolvedPath);
          }
        }

        const canonicalPath = `users/${uid}/projects/${projectId}/chapters/chapter-${cNum}.json`;
        if (!pathsToTry.includes(canonicalPath)) {
          pathsToTry.push(canonicalPath);
        }

        const prevVolId = (storyBible as any).previousVolumeId || (storyBible as any).seriesId;
        if (prevVolId) {
          const prevPath = `users/${uid}/projects/${prevVolId}/chapters/chapter-${cNum}.json`;
          if (!pathsToTry.includes(prevPath)) {
            pathsToTry.push(prevPath);
          }
        }

        let hasResolvedPathKey = false;
        if (chap && resolveChapterStoragePath(chap)) {
          hasResolvedPathKey = true;
        }

        for (const path of pathsToTry) {
          try {
            storageData = await loadChapterContentFromStorage(path);
            if (storageData && storageData.finalText) {
              isReadable = true;
              break;
            }
          } catch (e) {
            console.warn(`[Readiness Check] Failed to read storage path "${path}" for Chapter ${cNum}:`, e);
          }
        }

        const hasStorageFinalText = !!(storageData && storageData.finalText);
        const hasInMemoryFinalText = !!(chap && chap.content);
        const hasAnyFinalText = hasStorageFinalText || hasInMemoryFinalText;

        // Auditing
        if (chap) {
            console.log(`[Export Audit] Chapter ${cNum} Metadata:`, {
              chapterNumber: cNum,
              title: chap.title || null,
              hasFinalText: !!hasAnyFinalText,
              finalTextLength: storageData?.finalText?.length ?? chap.content?.length ?? null,
              storagePath: chap.storagePath || null,
              downloadUrlExists: !!chap.downloadUrl,
            });
        }

        return {
          cNum,
          isReadable,
          hasStorageFinalText,
          hasInMemoryFinalText,
          hasAnyFinalText,
          hasResolvedPathKey
        };
      });

      const results = await Promise.all(promises);

      for (const res of results) {
        if (res.hasResolvedPathKey) {
          countWithResolvedPath++;
        }
        if (res.isReadable) {
          countWithReadableJson++;
        }
        if (res.hasAnyFinalText) {
          countWithFinalText++;
          withFinal++;
        }

        // Determine if blocked or warning
        if (!res.isReadable) {
          if (res.hasInMemoryFinalText) {
            countRepairable++;
            warningsList.push(`Chapter ${res.cNum} files may exist in cache but storage paths are missing from metadata. Ready with cache fallback.`);
          } else {
            countTrulyMissing++;
            missingFilesList.push(res.cNum);
          }
        } else if (!res.hasStorageFinalText) {
          if (res.hasInMemoryFinalText) {
            countRepairable++;
            warningsList.push(`Chapter ${res.cNum} final text missing in storage but ready in cache fallback.`);
          } else {
            missingFinalList.push(res.cNum);
          }
        }
      }
      if (countWithFinalText > countWithMetadata) {
         console.log(`[EXPORT METADATA RECONCILED] ${JSON.stringify({
            projectId,
            oldMetadataCount: countWithMetadata,
            newMetadataCount: countWithFinalText,
            reason: "readable JSON/finalText count exceeded stale metadata"
         })}`);
         if (onRepairRequested) {
             // We trigger it asynchronously to avoid blocking the UI thread
             setTimeout(() => {
                 onRepairRequested();
             }, 500);
         }
      }
      
      if (!active) return;
      setWarnings(warningsList);
      setDiagnostics({
        chaptersWithMetadata: countWithMetadata,
        chaptersWithResolvedStoragePath: countWithResolvedPath,
        chaptersWithReadableStorageJson: countWithReadableJson,
        chaptersWithFinalText: countWithFinalText,
        chaptersRepairableFromFinalText: countRepairable,
        chaptersTrulyMissing: countTrulyMissing,
      });

      setReadiness(prev => ({
        ...prev,
        isChecking: false,
        chaptersFound: withFinal,
        chaptersWithFinalText: withFinal,
        missingFiles: missingFilesList,
        missingFinalText: missingFinalList
      }));
    };
    checkReadiness();
    return () => { active = false; };
  }, [chapters, checkTrigger]);

  const handleRepairMissingStoragePaths = async () => {
    setIsRepairing(true);
    setRepairStatus('Repairing missing storage paths...');
    try {
      const updatedChapters = [...chapters];
      let repairedCount = 0;

      for (let i = 0; i < updatedChapters.length; i++) {
        const chap = updatedChapters[i];
        const cNum = chap.chapterNumber || chap.id;
        const resolvedPath = resolveChapterStoragePath(chap);
        
        if (!resolvedPath && chap.content) {
          const storageData: StorageChapter = {
            chapterNumber: cNum,
            title: chap.title || `Chapter ${cNum}`,
            finalText: chap.content,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };

          const canonicalPath = await saveChapterContentToStorage(uid, projectId, storageData);
          updatedChapters[i] = {
            ...chap,
            storagePath: canonicalPath
          };
          repairedCount++;
        }
      }

      if (repairedCount > 0) {
        if (onUpdateChapters) {
          onUpdateChapters(updatedChapters);
        }
        
        const slimChapters = updatedChapters.map(c => {
          if (c.storagePath) {
            const { content, ...rest } = c;
            return rest;
          }
          return c;
        });
        
        const docRef = doc(db, 'users', uid, 'projects', projectId, 'state', 'current');
        try {
          await setDoc(docRef, { chapters: slimChapters }, { merge: true });
        } catch (err: any) {
          const isExhausted = 
            err.message?.includes('resource-exhausted') || 
            err.message?.includes('Quota limit exceeded') || 
            err.message?.includes('quota exceeded') ||
            err.code === 'resource-exhausted';
          if (isExhausted) {
            throw new Error("Firestore daily write quota has been reached. Repaired indices could not be updated in project state. Try again after quota resets.");
          }
          throw err;
        }
        
        setRepairStatus(`Successfully repaired ${repairedCount} chapters.`);
      } else {
        setRepairStatus('No chapters required repair.');
      }
      setCheckTrigger(prev => prev + 1);
    } catch (err: any) {
      console.error('Repair failed:', err);
      setRepairStatus(`Repair failed: ${err.message || err}`);
    } finally {
      setIsRepairing(false);
    }
  };

  const handleVerifyStorageFiles = async () => {
    setIsVerifying(true);
    setRepairStatus('Verifying storage files...');
    try {
      setCheckTrigger(prev => prev + 1);
      setRepairStatus('Verification complete.');
    } catch (err: any) {
      setRepairStatus(`Verification failed: ${err.message || err}`);
    } finally {
      setIsVerifying(false);
    }
  };

  const missingMetadataFields = [];
  if (!settings.bookTitle.trim()) missingMetadataFields.push("Book Title");
  if (!settings.authorName.trim()) missingMetadataFields.push("Author Name");
  if (!settings.copyrightYear.trim()) missingMetadataFields.push("Copyright Year");

  const isMetadataReady = missingMetadataFields.length === 0;
  const isContentReady = readiness.missingFiles.length === 0 && readiness.missingFinalText.length === 0 && readiness.chaptersFound >= readiness.totalExpected;
  const canExport = isMetadataReady && isContentReady && !readiness.isChecking;

  const [settingsLoading, setSettingsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchSettings = async () => {
      try {
        const docRef = doc(db, `users/${uid}/projects/${projectId}/publication/settings`);
        const snapshot = await getDoc(docRef);
        if (active && snapshot.exists()) {
          setSettings(prev => ({ ...prev, ...snapshot.data() }));
        }
      } catch (err) {
        console.error('Failed to load settings', err);
      } finally {
        if (active) setSettingsLoading(false);
      }
    };
    fetchSettings();
    return () => { active = false; };
  }, [uid, projectId]);

  useEffect(() => {
    if (settingsLoading) return;
    const saveSettings = async () => {
      try {
         await setDoc(doc(db, `users/${uid}/projects/${projectId}/publication/settings`), settings);
      } catch (e) {
         console.error('Failed to save settings', e);
      }
    };
    const timer = setTimeout(saveSettings, 1000);
    return () => clearTimeout(timer);
  }, [settings, settingsLoading, uid, projectId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleExport = async () => {
    setIsExporting(true);
    setError('');
    setExportUrl('');
    try {
      const result = await generatePublication(uid, projectId, storyBible, chapters, settings);
      setExportUrl(result.downloadUrl);

      const recordId = `exp_${Date.now()}`;
      const record: ExportRecord = {
        id: recordId,
        projectId,
        format: settings.exportFormat,
        fileName: result.fileName,
        storagePath: result.storagePath,
        downloadUrl: result.downloadUrl,
        createdAt: new Date().toISOString(),
        chapterCount: chapters.length,
        status: 'SUCCESS'
      };

      await setDoc(doc(db, `users/${uid}/projects/${projectId}/exports/${recordId}`), record);
      
      setPreviousExports(prev => [record, ...prev]);

    } catch (e: any) {
       setError(e.message || 'An error occurred during export.');
       const recordId = `exp_${Date.now()}`;
       const record: ExportRecord = {
        id: recordId,
        projectId,
        format: settings.exportFormat,
        fileName: 'failed',
        storagePath: '',
        createdAt: new Date().toISOString(),
        chapterCount: chapters.length,
        status: 'FAILED',
        errorMessage: e.message
      };
      await setDoc(doc(db, `users/${uid}/projects/${projectId}/exports/${recordId}`), record);
      setPreviousExports(prev => [record, ...prev]);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-cv-page/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-cv-panel border border-cv-border-strong rounded-2xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-cv-muted hover:text-cv-primary transition-colors p-2 bg-cv-elevated rounded-lg">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>

        <h2 className="text-2xl font-black text-cv-primary uppercase tracking-tighter mb-2 flex items-center gap-3">
          Publication Export
          <Tooltip text="Export your completed manuscript to DOCX or Markdown. The system will intelligently merge your manual edits with the generated text." />
        </h2>
        <p className="text-cv-muted text-sm mb-6">Configure formatting and optional matter to generate a production-ready file. This process is 100% deterministic and does not use AI.</p>

        {exportUrl ? (
           <div className="bg-cv-success/15 border border-cv-success/50 rounded-xl p-6 text-center space-y-4">
             <div className="w-16 h-16 bg-cv-success/20 rounded-full flex items-center justify-center mx-auto text-cv-success text-3xl">✓</div>
             <h3 className="text-xl font-bold text-cv-primary">Export Complete</h3>
             <p className="text-cv-secondary">Your file has been successfully formatted and uploaded to secure storage.</p>
             
             {previousExports[0] && previousExports[0].status === 'SUCCESS' && (
                <div className="bg-cv-success/10 border border-cv-success/40 rounded-lg p-3 inline-block text-left mb-4 mt-2">
                  <div className="text-cv-success font-bold uppercase text-[10px] tracking-widest mb-1">{previousExports[0].format} FORMAT</div>
                  <div className="text-cv-primary font-mono text-sm">{previousExports[0].fileName}</div>
                </div>
             )}

             <div className="pt-4 flex gap-4 justify-center">
                <a href={exportUrl} download={previousExports[0]?.fileName || "export.docx"} className="px-6 py-3 bg-cv-interactive hover:bg-cv-interactive-hover text-cv-page font-bold rounded-lg shadow-lg">Download File</a>
                <button onClick={() => setExportUrl('')} className="px-6 py-3 bg-cv-elevated hover:bg-cv-elevated text-cv-primary font-bold rounded-lg">Make Changes</button>
             </div>
           </div>
        ) : (
          <div className="space-y-8">
            <div className="bg-cv-page/50 border border-cv-border rounded-xl p-6">
              <h3 className="text-sm font-bold text-cv-gold uppercase tracking-widest border-b border-cv-border/50 pb-2 mb-4">Export Readiness Checklist</h3>
              {readiness.isChecking ? (
                <div className="text-cv-muted text-sm flex items-center"><div className="w-4 h-4 border-2 border-cv-border/30 border-t-cv-interactive rounded-full animate-spin mr-2"></div> Checking manuscript status...</div>
              ) : (
                <div className="space-y-4">
                  {/* Chapters vs Expected */}
                  <div className="bg-cv-panel border border-cv-border rounded p-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-cv-secondary font-medium">Chapters Expected vs Found</span>
                      {readiness.chaptersFound >= readiness.totalExpected ? (
                        <span className="text-cv-success font-bold bg-cv-success/10 px-2 py-1 rounded text-xs">Ready</span>
                      ) : (
                        <span className="text-cv-warning font-bold bg-amber-950 px-2 py-1 rounded text-xs">Needs attention</span>
                      )}
                    </div>
                    <div className="text-cv-muted text-xs mt-1">Found {readiness.chaptersFound} out of {readiness.totalExpected} expected chapters.</div>
                    {readiness.chaptersFound < readiness.totalExpected && (
                      <div className="text-cv-gold text-xs mt-2 bg-amber-950/30 p-2 rounded">
                        <strong>How to fix:</strong> Continue generating chapters until you reach the target chapter count in your story roadmap.
                      </div>
                    )}
                  </div>

                  {/* Missing Storage JSON Files */}
                  <div className="bg-cv-panel border border-cv-border rounded p-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-cv-secondary font-medium">Storage JSON Files</span>
                      {readiness.missingFiles.length === 0 ? (
                        <span className="text-cv-success font-bold bg-cv-success/10 px-2 py-1 rounded text-xs">Ready</span>
                      ) : (
                        <span className="text-cv-danger font-bold bg-red-950 px-2 py-1 rounded text-xs">Blocked</span>
                      )}
                    </div>
                    {readiness.missingFiles.length > 0 && (
                      <>
                        <div className="text-cv-danger/80 text-xs mt-1 font-mono">Missing: Chapters {readiness.missingFiles.join(', ')}</div>
                        <div className="text-cv-gold text-xs mt-2 bg-amber-950/30 p-2 rounded">
                          <strong>How to fix:</strong> {diagnostics.chaptersWithResolvedStoragePath < diagnostics.chaptersWithMetadata ? "Chapter files may exist, but storage paths are missing from metadata." : "Regenerate or repair these chapters so the full chapter file exists."}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Chapters with Final Text */}
                  <div className="bg-cv-panel border border-cv-border rounded p-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-cv-secondary font-medium">Final Text Completed</span>
                      {readiness.missingFinalText.length === 0 ? (
                        <span className="text-cv-success font-bold bg-cv-success/10 px-2 py-1 rounded text-xs">Ready</span>
                      ) : (
                        <span className="text-cv-danger font-bold bg-red-950 px-2 py-1 rounded text-xs">Blocked</span>
                      )}
                    </div>
                    {readiness.missingFinalText.length > 0 && (
                      <>
                        <div className="text-cv-danger/80 text-xs mt-1 font-mono">Missing: Chapters {readiness.missingFinalText.join(', ')}</div>
                        <div className="text-cv-gold text-xs mt-2 bg-amber-950/30 p-2 rounded">
                          <strong>How to fix:</strong> Open these chapters and run the final polish step before exporting.
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Core Metadata */}
                  <div className="bg-cv-panel border border-cv-border rounded p-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-cv-secondary font-medium">Core Metadata</span>
                      {isMetadataReady ? (
                        <span className="text-cv-success font-bold bg-cv-success/10 px-2 py-1 rounded text-xs">Ready</span>
                      ) : (
                        <span className="text-cv-danger font-bold bg-red-950 px-2 py-1 rounded text-xs">Blocked</span>
                      )}
                    </div>
                    {!isMetadataReady && (
                      <>
                        <div className="text-cv-danger/80 text-xs mt-1">Missing required fields: {missingMetadataFields.join(', ')}</div>
                        <div className="text-cv-gold text-xs mt-2 bg-amber-950/30 p-2 rounded">
                          <strong>How to fix:</strong> Add your {missingMetadataFields.join(', ')} in Publication Settings below.
                        </div>
                      </>
                    )}
                  </div>

                  {/* Diagnostic Summary & Repair Panel */}
                  <div className="bg-cv-page/40 border border-cv-border rounded-xl p-4 mt-4 space-y-4">
                    <h4 className="text-xs font-bold text-cv-gold uppercase tracking-widest">Diagnostic Summary & Repair</h4>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs font-mono">
                      <div className="bg-cv-panel border border-cv-border/80 p-2 rounded">
                        <div className="text-cv-muted text-[10px]">Metadata Found</div>
                        <div className="text-sm font-bold text-cv-primary">{diagnostics.chaptersWithMetadata}</div>
                      </div>
                      <div className="bg-cv-panel border border-cv-border/80 p-2 rounded">
                        <div className="text-cv-muted text-[10px]">Resolved Path</div>
                        <div className="text-sm font-bold text-cv-primary">{diagnostics.chaptersWithResolvedStoragePath}</div>
                      </div>
                      <div className="bg-cv-panel border border-cv-border/80 p-2 rounded">
                        <div className="text-cv-muted text-[10px]">Readable JSON</div>
                        <div className="text-sm font-bold text-cv-primary">{diagnostics.chaptersWithReadableStorageJson}</div>
                      </div>
                      <div className="bg-cv-panel border border-cv-border/80 p-2 rounded">
                        <div className="text-cv-muted text-[10px]">Has Final Text</div>
                        <div className="text-sm font-bold text-cv-primary">{diagnostics.chaptersWithFinalText}</div>
                      </div>
                      <div className="bg-cv-panel border border-cv-border/80 p-2 rounded">
                        <div className="text-cv-muted text-[10px]">Repairable</div>
                        <div className="text-sm font-bold text-cv-warning">{diagnostics.chaptersRepairableFromFinalText}</div>
                      </div>
                      <div className="bg-cv-panel border border-cv-border/80 p-2 rounded">
                        <div className="text-cv-muted text-[10px]">Truly Missing</div>
                        <div className="text-sm font-bold text-cv-danger">{diagnostics.chaptersTrulyMissing}</div>
                      </div>
                    </div>

                    {/* Collapsible Safe Metadata Inspection Audit */}
                    <details className="text-left bg-cv-panel/50 border border-cv-border p-2.5 rounded text-[11px] font-mono">
                      <summary className="cursor-pointer text-[10px] uppercase font-bold text-cv-muted select-none">
                        🔍 Inspect Chapter Paths & Metadata (Safe Audit)
                      </summary>
                      <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto pr-1">
                        {chapters.map(c => {
                          const cNum = c.chapterNumber || c.id;
                          const resolved = resolveChapterStoragePath(c);
                          return (
                            <div key={c.id} className="border-b border-cv-border/50 pb-1.5 mb-1.5 last:border-0 last:pb-0 last:mb-0">
                              <div className="flex justify-between font-bold text-cv-primary">
                                <span>Chapter {cNum}: {c.title || "No Title"}</span>
                                <span className={c.content ? "text-cv-success font-sans" : "text-cv-gold font-sans"}>
                                  {c.content ? "In-Memory Text" : "Stripped/Slim"}
                                </span>
                              </div>
                              <div className="text-[10px] text-cv-muted space-y-0.5 mt-0.5">
                                <div><span className="text-cv-muted">storagePath:</span> {c.storagePath || "undefined"}</div>
                                <div><span className="text-cv-muted">storageJsonPath:</span> {c.storageJsonPath || "undefined"}</div>
                                <div><span className="text-cv-muted">storageRef:</span> {c.storageRef || "undefined"}</div>
                                <div><span className="text-cv-muted">contentPath:</span> {c.contentPath || "undefined"}</div>
                                <div><span className="text-cv-muted">artifactPath:</span> {c.artifactPath || "undefined"}</div>
                                <div><span className="text-cv-muted">finalTextStoragePath:</span> {c.finalTextStoragePath || "undefined"}</div>
                                <div><span className="text-cv-muted">filePath:</span> {c.filePath || "undefined"}</div>
                                <div><span className="text-cv-muted">Resolved Path:</span> <span className="text-cv-gold">{resolved || "NULL"}</span></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </details>

                    {warnings.length > 0 && (
                      <div className="bg-amber-950/30 border border-amber-500/20 p-3 rounded text-cv-warning text-xs space-y-1">
                        <div className="font-bold flex items-center gap-1.5 text-cv-warning uppercase tracking-wider text-[10px]">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                          Ready with cached fallback (Warning)
                        </div>
                        <ul className="list-disc list-inside space-y-0.5 opacity-90 pl-1 font-sans text-[11px]">
                          {warnings.map((w, idx) => <li key={idx}>{w}</li>)}
                        </ul>
                      </div>
                    )}

                    {repairStatus && (
                      <div className="bg-cv-panel border border-cv-border/80 p-2.5 rounded text-cv-secondary text-xs font-mono">
                        {repairStatus}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2.5 pt-1">
                      <button
                        type="button"
                        onClick={handleRepairMissingStoragePaths}
                        disabled={isRepairing || diagnostics.chaptersRepairableFromFinalText === 0}
                        className="px-3 py-1.5 bg-cv-gold hover:bg-cv-gold-hover disabled:bg-cv-elevated disabled:text-cv-disabled text-cv-page text-[11px] font-bold rounded transition-colors flex items-center"
                      >
                        {isRepairing ? (
                          <><div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin mr-1.5"></div> Repairing...</>
                        ) : 'Repair Missing Storage Paths'}
                      </button>
                      <button
                        type="button"
                        onClick={handleVerifyStorageFiles}
                        disabled={isVerifying}
                        className="px-3 py-1.5 bg-cv-elevated hover:bg-cv-elevated disabled:bg-cv-elevated disabled:text-cv-disabled text-cv-secondary text-[11px] font-bold rounded transition-colors"
                      >
                        {isVerifying ? 'Verifying...' : 'Verify Storage Files'}
                      </button>
                    </div>
                  </div>

                </div>
              )}
            </div>

            {error && (
              <div className="bg-red-900/50 border border-cv-danger/50 p-4 rounded-lg text-red-200">
                <p className="font-bold">Export Failed</p>
                <p className="text-sm">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Metadata */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-cv-gold uppercase tracking-widest border-b border-cv-border pb-2">Core Metadata</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] font-bold text-cv-muted uppercase">Book Title</label>
                    <input name="bookTitle" value={settings.bookTitle} onChange={handleChange} className="w-full bg-cv-page border border-cv-border-strong rounded p-2 text-sm text-cv-primary" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-cv-muted uppercase">Author Name</label>
                    <input name="authorName" value={settings.authorName} onChange={handleChange} className="w-full bg-cv-page border border-cv-border-strong rounded p-2 text-sm text-cv-primary" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-bold text-cv-muted uppercase">Copyright Year</label>
                      <input name="copyrightYear" value={settings.copyrightYear} onChange={handleChange} className="w-full bg-cv-page border border-cv-border-strong rounded p-2 text-sm text-cv-primary" />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-cv-muted uppercase">Publisher (Optional)</label>
                      <input name="publisherName" value={settings.publisherName} onChange={handleChange} className="w-full bg-cv-page border border-cv-border-strong rounded p-2 text-sm text-cv-primary" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Formatting */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-cv-interactive uppercase tracking-widest border-b border-cv-border pb-2">Formatting</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] font-bold text-cv-muted uppercase">Export Format</label>
                    <select name="exportFormat" value={settings.exportFormat} onChange={handleChange} className="w-full bg-cv-page border border-cv-border-strong rounded p-2 text-sm text-cv-primary">
                      <option value="DOCX">DOCX (Word Document)</option>
                      <option value="MD">Markdown</option>
                      <option value="TXT">Plain Text</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-cv-muted uppercase">Heading Style</label>
                    <select name="headingStyle" value={settings.headingStyle} onChange={handleChange} className="w-full bg-cv-page border border-cv-border-strong rounded p-2 text-sm text-cv-primary">
                      <option value="STANDARD">Standard Left</option>
                      <option value="CENTERED">Centered</option>
                      <option value="BOLD">Bold / Emphasized</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-cv-muted uppercase">Paragraph Style</label>
                    <select name="paragraphStyle" value={settings.paragraphStyle} onChange={handleChange} className="w-full bg-cv-page border border-cv-border-strong rounded p-2 text-sm text-cv-primary">
                      <option value="INDENTED">First Line Indent (Traditional Book)</option>
                      <option value="SPACED">Spaced (Web/Article)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-cv-muted uppercase">Scene Break Style</label>
                    <select name="sceneBreakStyle" value={settings.sceneBreakStyle} onChange={handleChange} className="w-full bg-cv-page border border-cv-border-strong rounded p-2 text-sm text-cv-primary">
                      <option value="ASTERISKS">Asterisks (***)</option>
                      <option value="LINE">Line</option>
                      <option value="SPACE">Blank Space</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Matter Setup */}
            <div className="space-y-4">
               <h3 className="text-sm font-bold text-cv-success uppercase tracking-widest border-b border-cv-border pb-2">Front & Back Matter</h3>
               <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <label className="flex items-center gap-2 text-xs text-cv-secondary">
                    <input type="checkbox" name="includeTitlePage" checked={settings.includeTitlePage} onChange={handleChange} className="rounded border-cv-border-strong bg-cv-panel" /> Title Page
                  </label>
                  <label className="flex items-center gap-2 text-xs text-cv-secondary">
                    <input type="checkbox" name="includeCopyrightPage" checked={settings.includeCopyrightPage} onChange={handleChange} className="rounded border-cv-border-strong bg-cv-panel" /> Copyright Page
                  </label>
                  <label className="flex items-center gap-2 text-xs text-cv-secondary">
                    <input type="checkbox" name="includeToc" checked={settings.includeToc} onChange={handleChange} className="rounded border-cv-border-strong bg-cv-panel" /> Table of Contents
                  </label>
                  <label className="flex items-center gap-2 text-xs text-cv-secondary">
                    <input type="checkbox" name="includeDedication" checked={settings.includeDedication} onChange={handleChange} className="rounded border-cv-border-strong bg-cv-panel" /> Dedication
                  </label>
                  <label className="flex items-center gap-2 text-xs text-cv-secondary">
                    <input type="checkbox" name="includeAcknowledgments" checked={settings.includeAcknowledgments} onChange={handleChange} className="rounded border-cv-border-strong bg-cv-panel" /> Acknowledgments
                  </label>
                  <label className="flex items-center gap-2 text-xs text-cv-secondary">
                    <input type="checkbox" name="includeAboutAuthor" checked={settings.includeAboutAuthor} onChange={handleChange} className="rounded border-cv-border-strong bg-cv-panel" /> About Author
                  </label>
                  <label className="flex items-center gap-2 text-xs text-cv-secondary">
                    <input type="checkbox" name="includeAlsoBy" checked={settings.includeAlsoBy} onChange={handleChange} className="rounded border-cv-border-strong bg-cv-panel" /> Also By
                  </label>
               </div>

               {/* Conditional Text Areas */}
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                 {settings.includeDedication && (
                    <div>
                      <label className="text-[10px] font-bold text-cv-muted uppercase">Dedication Text</label>
                      <textarea name="dedication" value={settings.dedication} onChange={handleChange} className="w-full h-20 bg-cv-page border border-cv-border-strong rounded p-2 text-sm text-cv-primary resize-none" placeholder="For..." />
                    </div>
                 )}
                 {settings.includeAcknowledgments && (
                    <div>
                      <label className="text-[10px] font-bold text-cv-muted uppercase">Acknowledgments</label>
                      <textarea name="acknowledgments" value={settings.acknowledgments} onChange={handleChange} className="w-full h-20 bg-cv-page border border-cv-border-strong rounded p-2 text-sm text-cv-primary resize-none" />
                    </div>
                 )}
                 {settings.includeAboutAuthor && (
                    <div>
                      <label className="text-[10px] font-bold text-cv-muted uppercase">About the Author</label>
                      <textarea name="aboutAuthor" value={settings.aboutAuthor} onChange={handleChange} className="w-full h-20 bg-cv-page border border-cv-border-strong rounded p-2 text-sm text-cv-primary resize-none" />
                    </div>
                 )}
                 {settings.includeAlsoBy && (
                    <div>
                      <label className="text-[10px] font-bold text-cv-muted uppercase">Also By</label>
                      <textarea name="alsoBy" value={settings.alsoBy} onChange={handleChange} className="w-full h-20 bg-cv-page border border-cv-border-strong rounded p-2 text-sm text-cv-primary resize-none" placeholder="Book 1&#10;Book 2" />
                    </div>
                 )}
               </div>
            </div>

            {previousExports.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-cv-muted uppercase tracking-widest border-b border-cv-border pb-2">Previous Exports</h3>
                <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                  {previousExports.map((exp) => (
                    <div key={exp.id} className="bg-cv-page border border-cv-border rounded p-3 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${exp.status === 'SUCCESS' ? 'bg-emerald-900/50 text-cv-success' : 'bg-red-900/50 text-cv-danger'}`}>
                            {exp.format}
                          </span>
                          <span className="text-sm font-medium text-cv-secondary">{exp.fileName}</span>
                        </div>
                        <div className="text-[10px] text-cv-muted mt-1">
                          {new Date(exp.createdAt).toLocaleString()} · {exp.chapterCount} chapters
                          {exp.errorMessage && <span className="text-cv-danger ml-2">Error: {exp.errorMessage}</span>}
                        </div>
                      </div>
                      {exp.status === 'SUCCESS' && exp.downloadUrl && (
                        <a href={exp.downloadUrl} download={exp.fileName} className="px-3 py-1.5 bg-cv-elevated hover:bg-cv-elevated text-cv-secondary text-xs font-bold rounded transition-colors">
                          Download
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-6 border-t border-cv-border flex justify-end gap-4">
              <button onClick={onClose} disabled={isExporting} className="px-6 py-3 text-cv-muted hover:text-cv-primary font-bold transition-colors">Cancel</button>
              <button 
                onClick={handleExport}
                disabled={!canExport || isExporting}
                title={!canExport ? "Please resolve Export Readiness Checklist items before generating the publication." : ""}
                className="px-8 py-3 bg-cv-interactive hover:bg-cv-interactive-hover disabled:bg-cv-elevated text-cv-primary font-bold rounded-lg shadow-lg flex items-center justify-center min-w-[200px]"
              >
                {isExporting ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div> Formatting...</>
                ) : 'Export Manuscript'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
